import { Org, OrgApi } from '@/models'
import { processData } from 'trip-vis'
import { cloneWithOverrides } from 'trip-vars'
import { PolyRegistry } from 'trip-vis'

function err(reason) {
  let error = new Error(reason)
  error.reason = reason
  throw error
}

async function findOccurs({ fetcher, fromDate, untilDate, vehicleIds }) {
  let data = await fetcher.get('/occurrences', {
    fromDate,
    untilDate,
    vehicleIds,
    type: 'infraction',
    includeConfigRules: true,
    limit: 300
  })

  return data.rows
}

async function findPolys({ fetcher }) {
  return fetcher.get('/polygons')
}

async function findBanners({ fromDate, untilDate, vehicleIds, vars, fetcher }) {
  let filter = {
    fromDate,
    untilDate,
    vehicleIds
  }

  // todos, sem grupo
  if (vars.includes('worklogDetails')) return findWorklogs(fetcher, filter)

  // grupos
  if (vars.includes('worklogGroups'))
    return findWorklogs({
      fetcher,
      ...filter,
      useGroups: true
    })

  // ciclos apenas
  if (vars.includes('worklogCycles')) return findCycles(fetcher, filter)

  // transits apenas
  if (vars.includes('cycleEndpoints')) return findTransits(fetcher, filter)

  return []
}

const strTypes = {
  workshiftChange: 'Troca de turno',
  movement: 'Deslocamento',
  meal: 'Refeição',
  waitingQueue: 'Aguardando na fila',
  load: 'Trab. por tonelada (carregando)',
  movingToLoad: 'Trab. por tonelada (indo carregar)',
  unload: 'Trab. por tonelada (descarregando)',
  movingToUnload: 'Trab. por tonelada (indo descarregar)',
  group: 'Trabalhando por tonelada',
  available: 'Não-trabalhando, disponível',
  maintenance: 'Não-trabalhando, manutenção'
}

async function findWorklogs(fetcher, filter) {
  let data = await fetcher.get('/worklogs', {
    ...filter,
    limit: 300
  })
  return data.rows.map(r => {
    r.label = strTypes[r.type] || '??'
    r.cssClass = r.type
    return r
  })
}

async function findTransits(fetcher, filter) {
  try {
    let data = await fetcher.get('/transits', {
      ...filter,
      limit: 300
    })
    return data.rows.map(r => {
      let name = r.endPolygon && r.endPolygon.name
      return {
        startTimestamp: r.startTimestamp,
        endTimestamp: r.endTimestamp,
        label: `P/ ${name || 'desconhecido'} (#${r.id}}`,
        cssClass: 'cycle'
      }
    })
  } catch ( err ) {
    return []
  }
}

async function findCycles(fetcher, filter) {
  let data = await fetcher.get('/worklogs/cycles', {
    ...filter,
    limit: 300
  })

  return data.rows.map(r => {
    let unload = r.unload,
      extraInfo = unload.extraInfo
    return {
      unloadId: unload.id,
      startTimestamp: extraInfo.cycleStartTimestamp,
      endTimestamp: extraInfo.cycleEndTimestamp,
      cycleIds: extraInfo.cycleIds,
      durationS: extraInfo.cycleDurationS,
      label: 'Ciclo #' + unload.id,
      cssClass: 'cycle'
    }
  })
}

export default async function FetchVisData({
  orgCode,
  tripId,
  vars,
  chartHeightPx,
  isMobile = false
}) {
  try {
    let fetcher = new OrgApi(orgCode)

    // eslint-disable-next-line
    console.log(
      'Building TripVis for trip %s/%s, vars %s',
      orgCode,
      tripId,
      vars.join(',')
    )

    // Para funcionar o an4speed, precisamos modificar os
    // vehicleConfigs e as vars que são pedidas.
    let hasAn4Speed = false
    vars = [...vars]
    if ( vars.includes('an4speed') ) {
      hasAn4Speed = true
      vars = vars.map(x => x == 'an4speed' ? 'an4' : x)
    }

    // FIXME: O objeto retornado pela api /streams é uma Trip com
    // propriedades extras. Isto não foi uma boa ideia porque sempre
    // será necessário modificar (e.g. dash/features/trips/parser.js)
    // quando uma nova coluna é adicionada na Trip (e.g. lapseCount,
    // serial, etc).
    //
    // Para exibir corretamente no Admin, iremos buscar novamente os
    // dados do trecho e guardar em outro objeto.
    let mainTrip = await fetcher.get(`trips/${tripId}`),
      org = await Org.findById(orgCode)

    // Primeiro busca as streams para obter
    // start/endTimestamp e vehicleId
    let trip = await fetcher.get(`trips/${tripId}/streams`, { vars })

    if (trip == null || trip.id == null) err('NoSuchTrip')
    if (trip.missingRawdata == true) err('MissingRawdata')

    let fromDate = trip.startTimestamp,
      untilDate = trip.endTimestamp,
      vehicleIds = [trip.vehicle.id],
      filter = { fromDate, untilDate, vehicleIds }

    let occurs = await findOccurs({ fetcher, ...filter }),
      polys = await findPolys({ fetcher }),
      banners = await findBanners({ fetcher, ...filter, vars })

    let polyRegistry = new PolyRegistry(polys)
    let vehicleCfgs = trip.vehicle.configs

    // Se temos a variável an4speed selecionada, então
    // modifica a config para que a escala desta var seja
    // "speed".
    if (hasAn4Speed) {
      if (vehicleCfgs == null) vehicleCfgs = {}
      vehicleCfgs.an4scale = 'speed'
    }

    let varRegistry = cloneWithOverrides(vehicleCfgs)

    let data = {
      opts: {
        isRoot: true,
        chartHeightPx,
        isMobile
      },
      varCodes: vars,
      trips: [trip],
      streams: {
        occurs,
        banners
      },
      mainTrip,
      org
    }

    processData({ data, varRegistry, polyRegistry })

    return data
  } catch (err) {
    let status = err.status

    // eslint-disable-next-line
    console.log(
      'Error fetching vis data: err.status %s, err.body %s',
      status,
      JSON.stringify(err.body),
      err.stack
    )

    if (err.reason == null) {
      if (status == 400 || status == 404) err.reason = 'NoSuchTrip'
      else if (status == 500) err.reason = 'ServerError'
      else if (status == null) err.reason = 'VisError'
    }

    err.reqId = err.body && err.body.reqId
    throw err
  }
}
